import { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { FlaskConical, Copy, Check, ClipboardList } from "lucide-react"

type ChartListingRow = {
  id: number
  chart_date: string | null
  position: number
  recording_id: number
  recording_title: string
  recording_slug: string
  artist_name: string | null
  recording_type: string | null
  completeness_score: number
}

type Filters = {
  title?: string
  date_from?: string
  date_to?: string
  recording_type?: string
  pos_from?: string
  pos_to?: string
}

type Props = {
  chart_listings: ChartListingRow[]
  total_count: number
  capped: boolean
  recording_types: string[]
  filters: Filters
  sort: string
  dir: "asc" | "desc"
}

const ENTITY_TABS = [
  { label: "Recordings",      href: "/admin/research/recordings" },
  { label: "People",          href: "/admin/research/people" },
  { label: "Gigs",            href: "/admin/research/gigs" },
  { label: "Chart Listings",  href: "/admin/research/chart_listings" },
]

const ic = "w-full rounded border border-hairline bg-page px-3 py-1.5 text-sm text-ink-body placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-current"
const sc = "w-full rounded border border-hairline bg-page px-3 py-1.5 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-current"

function formatAuDate(iso: string | null): string {
  if (!iso) return "—"
  const [y, m, d] = iso.split("-")
  return `${d}/${m}/${y}`
}

export default function ResearchChartListings({ chart_listings, total_count, capped, recording_types, filters, sort, dir }: Props) {
  const [form, setForm] = useState<Filters>(filters)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [detailsText, setDetailsText] = useState("")
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [copied, setCopied] = useState(false)

  function toggleSelected(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    setSelectedIds((prev) => (prev.size === chart_listings.length ? new Set() : new Set(chart_listings.map((c) => c.id))))
  }

  async function showDetails() {
    setLoadingDetails(true)
    setCopied(false)
    try {
      const recordingIds = Array.from(new Set(chart_listings.filter((c) => selectedIds.has(c.id)).map((c) => c.recording_id)))
      const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
      const response = await fetch("/admin/research/recordings/export", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrf,
          Accept: "application/json",
        },
        body: new URLSearchParams(recordingIds.map((id) => [ "ids[]", String(id) ])).toString(),
      })
      const data = await response.json()
      setDetailsText(data.text)
      setDetailsOpen(true)
    } finally {
      setLoadingDetails(false)
    }
  }

  function copyDetails() {
    navigator.clipboard.writeText(detailsText).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  function buildAllParams(overrides: Record<string, string> = {}): Record<string, string> {
    const p: Record<string, string> = {}
    Object.entries(form).forEach(([k, v]) => { if (v) p[k] = v })
    if (sort && sort !== "chart_date") p.sort = sort
    if (dir && dir !== "desc") p.dir = dir
    return { ...p, ...overrides }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    router.get("/admin/research/chart_listings", buildAllParams(), { preserveState: false })
  }

  function handleReset() {
    setForm({})
    router.get("/admin/research/chart_listings", {}, { preserveState: false })
  }

  function handleSort(col: string) {
    const newDir = sort === col && dir === "desc" ? "asc" : "desc"
    router.get("/admin/research/chart_listings", buildAllParams({ sort: col, dir: newDir }), { preserveState: false })
  }

  function SortHeader({ col, children }: { col: string; children: React.ReactNode }) {
    const active = sort === col
    return (
      <th className="pb-2 pr-4 font-medium">
        <button onClick={() => handleSort(col)} className="flex items-center gap-1 hover:text-ink-body transition-colors whitespace-nowrap">
          {children}
          <span className={active ? "text-ink-body" : "opacity-30"}>
            {active ? (dir === "asc" ? "↑" : "↓") : "↕"}
          </span>
        </button>
      </th>
    )
  }

  const isFiltering = Object.values(filters).some(Boolean)

  return (
    <>
      <Head title="Research: Chart Listings">
        <meta name="description" content="Filter and search chart listings by field." />
        <meta property="og:title" content="Research: Chart Listings" />
        <meta property="og:description" content="Filter and search chart listings by field." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex items-center gap-2 mb-1">
            <FlaskConical className="h-5 w-5 text-ink-muted" />
            <h1>Research</h1>
          </div>
          <div className="flex gap-4 mt-3 border-b border-hairline -mx-6 sm:-mx-10 px-6 sm:px-10">
            {ENTITY_TABS.map((tab) => (
              <Link
                key={tab.href}
                href={tab.href}
                className={[
                  "pb-3 text-sm font-medium no-underline border-b-2 -mb-px transition-colors",
                  tab.label === "Chart Listings"
                    ? "border-current text-ink-body"
                    : "border-transparent text-ink-muted hover:text-ink-body"
                ].join(" ")}
              >
                {tab.label}
              </Link>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div className="col-span-2 sm:col-span-3">
            <label className="block text-xs font-medium text-ink-muted mb-1">Recording title</label>
            <input name="title" type="text" value={form.title ?? ""} onChange={handleChange} placeholder="e.g. Eagle Rock" className={ic} />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">Chart date (from)</label>
            <input name="date_from" type="date" value={form.date_from ?? ""} onChange={handleChange} className={ic} />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">Chart date (to)</label>
            <input name="date_to" type="date" value={form.date_to ?? ""} onChange={handleChange} className={ic} />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">Recording type</label>
            <select name="recording_type" value={form.recording_type ?? ""} onChange={handleChange} className={sc}>
              <option value="">All types</option>
              {recording_types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">Chart position (from)</label>
            <input name="pos_from" type="number" value={form.pos_from ?? ""} onChange={handleChange} placeholder="e.g. 1" className={ic} min={1} />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">Chart position (to)</label>
            <input name="pos_to" type="number" value={form.pos_to ?? ""} onChange={handleChange} placeholder="e.g. 10" className={ic} min={1} />
          </div>
          <div className="col-span-2 sm:col-span-3 flex items-center gap-3 pt-1">
            <Button type="submit">Search</Button>
            {isFiltering && (
              <button type="button" onClick={handleReset} className="text-sm text-ink-muted hover:text-ink-body transition-colors">
                Clear filters
              </button>
            )}
          </div>
        </form>

        <div className="mt-4 flex items-center gap-3 flex-wrap">
          {isFiltering && (
            <p className="text-sm text-ink-muted">
              {capped
                ? `Showing first 200 of ${total_count} results`
                : `${total_count} ${total_count === 1 ? "result" : "results"}`}
            </p>
          )}
          {selectedIds.size > 0 && (
            <Button type="button" onClick={showDetails} disabled={loadingDetails} className="inline-flex items-center gap-1.5">
              <ClipboardList className="w-3.5 h-3.5" />
              {loadingDetails ? "Loading…" : `Copy details (${selectedIds.size})`}
            </Button>
          )}
        </div>

        {isFiltering && chart_listings.length === 0 ? (
          <p className="mt-8 text-center text-sm text-ink-muted">No chart listings match your filters.</p>
        ) : chart_listings.length > 0 ? (
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline text-left text-xs text-ink-muted">
                  <th className="pb-2 pr-4 font-medium w-4">
                    <Checkbox
                      checked={chart_listings.length > 0 && selectedIds.size === chart_listings.length}
                      onChange={toggleSelectAll}
                      aria-label="Select all chart listings"
                    />
                  </th>
                  <SortHeader col="chart_date">Chart date</SortHeader>
                  <SortHeader col="position">Position</SortHeader>
                  <SortHeader col="title">Title</SortHeader>
                  <th className="pb-2 pr-4 font-medium">Artist</th>
                  <th className="pb-2 pr-4 font-medium">Type</th>
                  <SortHeader col="completeness_score">Score</SortHeader>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {chart_listings.map((c) => (
                  <tr key={c.id} className="hover:bg-surface">
                    <td className="py-2 pr-4">
                      <Checkbox
                        checked={selectedIds.has(c.id)}
                        onChange={() => toggleSelected(c.id)}
                        aria-label={`Select ${c.recording_title}`}
                      />
                    </td>
                    <td className="py-2 pr-4 text-ink-muted tabular-nums whitespace-nowrap">{formatAuDate(c.chart_date)}</td>
                    <td className="py-2 pr-4 text-ink-muted tabular-nums">#{c.position}</td>
                    <td className="py-2 pr-4">
                      <Link href={`/recordings/${c.recording_slug}`} className="font-medium text-ink-body hover:text-amber-600 dark:hover:text-amber-400 no-underline">
                        {c.recording_title}
                      </Link>
                    </td>
                    <td className="py-2 pr-4 text-ink-muted">{c.artist_name ?? "—"}</td>
                    <td className="py-2 pr-4 text-ink-muted">{c.recording_type ?? "—"}</td>
                    <td className="py-2 tabular-nums">
                      <span className={`font-medium ${c.completeness_score >= 7 ? "text-green-600 dark:text-green-400" : c.completeness_score >= 4 ? "text-amber-600 dark:text-amber-400" : "text-red-500 dark:text-red-400"}`}>
                        {c.completeness_score}/9
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent size="lg">
            <DialogHeader>
              <DialogTitle>Recording details</DialogTitle>
            </DialogHeader>
            <textarea
              readOnly
              value={detailsText}
              rows={16}
              className="w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body font-mono focus:outline-none focus:ring-1 focus:ring-current"
            />
            <DialogFooter>
              <Button type="button" onClick={copyDetails} className="inline-flex items-center gap-1.5">
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy to clipboard"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </AdminShell>
    </>
  )
}
